import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';

const CANVAS_WIDTH = 640;
const CANVAS_HEIGHT = 250;
const MIN_SIZE = 20;
const MAX_IMAGE_SIZE = 300 * 1024;
const HANDLE_SIZE = 8;
const CORNERS = ['nw', 'ne', 'sw', 'se'];

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

const escapeHtml = (str) => String(str)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const stripHtml = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// A assinatura é persistida como HTML com um atributo data-signature-layers para permitir reabrir e continuar editando as camadas
const parseSignatureValue = (value) => {
  if (!value) return [];
  const match = value.match(/data-signature-layers="([^"]*)"/);
  if (match) {
    try {
      const parsed = JSON.parse(decodeURIComponent(match[1]));
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // ignora e cai no fallback de texto simples abaixo
    }
  }
  const text = stripHtml(value).trim();
  if (!text) return [];
  return [{ id: 'layer-legacy-text', type: 'text', x: 10, y: 10, width: 300, height: 60, value: text, fontSize: 14, color: '#111827' }];
};

const buildSignatureHtml = (layers) => {
  if (!layers.length) return '';
  const layersJson = encodeURIComponent(JSON.stringify(layers));
  const inner = layers.map((layer) => {
    if (layer.type === 'image') {
      return `<img src="${layer.value}" alt="Assinatura" style="position:absolute;left:${layer.x}px;top:${layer.y}px;width:${layer.width}px;height:${layer.height}px;object-fit:contain;" />`;
    }
    return `<div style="position:absolute;left:${layer.x}px;top:${layer.y}px;width:${layer.width}px;height:${layer.height}px;font-size:${layer.fontSize}px;color:${layer.color};font-family:Arial, sans-serif;white-space:pre-wrap;">${escapeHtml(layer.value)}</div>`;
  }).join('');
  return `<div data-signature-layers="${layersJson}" style="position:relative;width:${CANVAS_WIDTH}px;height:${CANVAS_HEIGHT}px;">${inner}</div>`;
};

const getHandleStyle = (corner) => {
  const style = {
    position: 'absolute',
    width: HANDLE_SIZE,
    height: HANDLE_SIZE,
    background: '#3b82f6',
    borderRadius: 2,
    zIndex: 1
  };
  style.cursor = `${corner}-resize`;
  if (corner.includes('n')) style.top = -HANDLE_SIZE / 2;
  if (corner.includes('s')) style.bottom = -HANDLE_SIZE / 2;
  if (corner.includes('w')) style.left = -HANDLE_SIZE / 2;
  if (corner.includes('e')) style.right = -HANDLE_SIZE / 2;
  return style;
};

const SignatureCanvasEditor = ({ value, onChange, toastRef, onEditText, onRestoreText }) => {
  const [layers, setLayers] = useState(() => parseSignatureValue(value));
  const [selectedLayerId, setSelectedLayerId] = useState(null);

  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragStateRef = useRef(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onChangeRef.current?.(buildSignatureHtml(layers));
  }, [layers]);

  const selectedLayer = useMemo(
    () => layers.find((l) => l.id === selectedLayerId) || null,
    [layers, selectedLayerId]
  );

  const updateLayer = (id, updates) => {
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, ...updates } : l)));
  };

  const addLayer = (partial) => {
    const id = `layer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setLayers((prev) => [...prev, { id, fontSize: 14, color: '#111827', ...partial }]);
    setSelectedLayerId(id);
  };

  const addTextLayer = () => {
    addLayer({ type: 'text', x: 10, y: 10, width: 220, height: 32, value: 'Texto da assinatura' });
  };

  const deleteSelectedLayer = () => {
    if (!selectedLayerId) return;
    setLayers((prev) => prev.filter((l) => l.id !== selectedLayerId));
    setSelectedLayerId(null);
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toastRef?.current?.show({ severity: 'warn', summary: 'Atenção', detail: 'Selecione um arquivo de imagem válido' });
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toastRef?.current?.show({ severity: 'warn', summary: 'Atenção', detail: 'A imagem deve ter no máximo 300KB' });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      const img = new Image();
      img.onload = () => {
        const maxWidth = 220;
        const scale = img.naturalWidth > maxWidth ? maxWidth / img.naturalWidth : 1;
        const width = clamp(Math.round(img.naturalWidth * scale), MIN_SIZE, CANVAS_WIDTH - 10);
        const height = clamp(Math.round(img.naturalHeight * scale), MIN_SIZE, CANVAS_HEIGHT - 10);
        addLayer({ type: 'image', x: 10, y: 10, width, height, value: src });
      };
      img.src = src;
    };
    reader.onerror = () => {
      toastRef?.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao ler a imagem selecionada' });
    };
    reader.readAsDataURL(file);
  };

  const handleLayerMouseDown = (e, layer) => {
    e.stopPropagation();
    setSelectedLayerId(layer.id);
    dragStateRef.current = {
      mode: 'move',
      layerId: layer.id,
      startX: e.clientX,
      startY: e.clientY,
      orig: { x: layer.x, y: layer.y, width: layer.width, height: layer.height }
    };
  };

  const handleResizeMouseDown = (e, layer, corner) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedLayerId(layer.id);
    dragStateRef.current = {
      mode: 'resize',
      corner,
      layerId: layer.id,
      startX: e.clientX,
      startY: e.clientY,
      orig: { x: layer.x, y: layer.y, width: layer.width, height: layer.height }
    };
  };

  const handleCanvasMouseDown = (e) => {
    if (e.target === canvasRef.current) {
      setSelectedLayerId(null);
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const { x, y, width, height } = drag.orig;

      if (drag.mode === 'move') {
        setLayers((prev) => prev.map((l) => (l.id === drag.layerId
          ? { ...l, x: clamp(x + dx, 0, CANVAS_WIDTH - width), y: clamp(y + dy, 0, CANVAS_HEIGHT - height) }
          : l)));
        return;
      }

      let newX = x;
      let newY = y;
      let newWidth = width;
      let newHeight = height;

      if (drag.corner.includes('e')) newWidth = clamp(width + dx, MIN_SIZE, CANVAS_WIDTH - x);
      if (drag.corner.includes('s')) newHeight = clamp(height + dy, MIN_SIZE, CANVAS_HEIGHT - y);
      if (drag.corner.includes('w')) {
        const proposedWidth = clamp(width - dx, MIN_SIZE, x + width);
        newX = x + width - proposedWidth;
        newWidth = proposedWidth;
      }
      if (drag.corner.includes('n')) {
        const proposedHeight = clamp(height - dy, MIN_SIZE, y + height);
        newY = y + height - proposedHeight;
        newHeight = proposedHeight;
      }

      setLayers((prev) => prev.map((l) => (l.id === drag.layerId
        ? { ...l, x: newX, y: newY, width: newWidth, height: newHeight }
        : l)));
    };

    const handleMouseUp = () => {
      dragStateRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const renderLayer = (layer) => {
    const isSelected = layer.id === selectedLayerId;
    const style = {
      position: 'absolute',
      left: layer.x,
      top: layer.y,
      width: layer.width,
      height: layer.height,
      cursor: 'move',
      outline: isSelected ? '2px solid #3b82f6' : '1px dashed transparent',
      boxSizing: 'border-box'
    };

    return (
      <div key={layer.id} style={style} onMouseDown={(e) => handleLayerMouseDown(e, layer)}>
        {layer.type === 'image' ? (
          <img
            src={layer.value}
            alt="Assinatura"
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              fontSize: layer.fontSize,
              color: layer.color,
              fontFamily: 'Arial, sans-serif',
              whiteSpace: 'pre-wrap',
              overflow: 'hidden',
              pointerEvents: 'none'
            }}
          >
            {layer.value}
          </div>
        )}
        {isSelected && CORNERS.map((corner) => (
          <div key={corner} style={getHandleStyle(corner)} onMouseDown={(e) => handleResizeMouseDown(e, layer, corner)} />
        ))}
      </div>
    );
  };

  return (
    <div className="flex gap-3 flex-wrap">
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <Button label="Inserir Texto" icon="pi pi-plus" className="p-button-outlined p-button-sm" onClick={addTextLayer} type="button" />
          <Button label="Inserir Imagem" icon="pi pi-image" className="p-button-outlined p-button-sm" onClick={() => fileInputRef.current?.click()} type="button" />
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageFileChange} />
          {onEditText && (
            <Button
              label="Editar Texto"
              icon="pi pi-file-edit"
              className="p-button-outlined p-button-sm"
              onClick={onEditText}
              type="button"
            />
          )}
          <Button
            label="Restaurar Texto"
            icon="pi pi pi-refresh"
            className="p-button-outlined p-button-sm"
            onClick={onRestoreText}
            type="button"
          />
        </div>
        <div
          ref={canvasRef}
          onMouseDown={handleCanvasMouseDown}
          className="relative border-2 border-dashed border-gray-300 bg-white overflow-hidden select-none"
          style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
        >
          {layers.map(renderLayer)}
        </div>
        <span className="text-xs text-gray-500">Arraste para mover e use os cantos para redimensionar.</span>
      </div>

      {selectedLayer && (
        <div className="flex flex-col gap-2 w-56 p-3 border border-gray-200 rounded-md bg-gray-50 text-xs">
          <span className="font-semibold text-gray-600">
            {selectedLayer.type === 'image' ? 'Propriedades da Imagem' : 'Propriedades do Texto'}
          </span>
          {selectedLayer.type === 'text' && (
            <>
              <label className="font-medium text-gray-500">Texto</label>
              <textarea
                className="w-full p-1 border border-gray-300 rounded text-xs"
                rows={2}
                value={selectedLayer.value}
                onChange={(e) => updateLayer(selectedLayer.id, { value: e.target.value })}
              />
              <label className="font-medium text-gray-500">Tamanho da fonte (px)</label>
              <input
                type="number"
                min={8}
                max={72}
                className="w-full p-1 border border-gray-300 rounded text-xs"
                value={selectedLayer.fontSize}
                onChange={(e) => updateLayer(selectedLayer.id, { fontSize: Number(e.target.value) || 14 })}
              />
              <label className="font-medium text-gray-500">Cor</label>
              <input
                type="color"
                className="w-full h-7 p-0 border border-gray-300 rounded"
                value={selectedLayer.color}
                onChange={(e) => updateLayer(selectedLayer.id, { color: e.target.value })}
              />
            </>
          )}
          <Button label="Excluir Camada" icon="pi pi-trash" severity="danger" size="small" className="p-button-outlined mt-1" onClick={deleteSelectedLayer} type="button" />
        </div>
      )}
    </div>
  );
};

export default SignatureCanvasEditor;
